// Cross-Module Integration Types - TypeScript-First Approach
// ✅ NO MAGIC STRINGS: Use typed enums and constants

/**
 * Cross-module event types
 */
export enum CrossModuleEvent {
  // User-Organization interactions
  USER_ORGANIZATION_JOINED = "USER_ORGANIZATION_JOINED",
  USER_ORGANIZATION_LEFT = "USER_ORGANIZATION_LEFT",
  USER_ORGANIZATION_ROLE_CHANGED = "USER_ORGANIZATION_ROLE_CHANGED",

  // User-Project interactions
  USER_PROJECT_ACCESS_GRANTED = "USER_PROJECT_ACCESS_GRANTED",
  USER_PROJECT_ACCESS_REVOKED = "USER_PROJECT_ACCESS_REVOKED",
  USER_PROJECT_ROLE_CHANGED = "USER_PROJECT_ROLE_CHANGED",

  // User-Application interactions
  USER_APPLICATION_ACCESS_GRANTED = "USER_APPLICATION_ACCESS_GRANTED",
  USER_APPLICATION_ACCESS_REVOKED = "USER_APPLICATION_ACCESS_REVOKED",
  USER_APPLICATION_ROLE_CHANGED = "USER_APPLICATION_ROLE_CHANGED",

  // Organization-Project interactions
  ORGANIZATION_PROJECT_CREATED = "ORGANIZATION_PROJECT_CREATED",
  ORGANIZATION_PROJECT_DELETED = "ORGANIZATION_PROJECT_DELETED",
  ORGANIZATION_PROJECT_UPDATED = "ORGANIZATION_PROJECT_UPDATED",

  // Organization-Application interactions
  ORGANIZATION_APPLICATION_CREATED = "ORGANIZATION_APPLICATION_CREATED",
  ORGANIZATION_APPLICATION_DELETED = "ORGANIZATION_APPLICATION_DELETED",
  ORGANIZATION_APPLICATION_UPDATED = "ORGANIZATION_APPLICATION_UPDATED",

  // Project-Application interactions
  PROJECT_APPLICATION_CREATED = "PROJECT_APPLICATION_CREATED",
  PROJECT_APPLICATION_DELETED = "PROJECT_APPLICATION_DELETED",
  PROJECT_APPLICATION_UPDATED = "PROJECT_APPLICATION_UPDATED",

  // Auth interactions
  AUTH_USER_LOGIN = "AUTH_USER_LOGIN",
  AUTH_USER_LOGOUT = "AUTH_USER_LOGOUT",
  AUTH_USER_SESSION_EXPIRED = "AUTH_USER_SESSION_EXPIRED",

  // Data synchronization
  DATA_SYNC_REQUESTED = "DATA_SYNC_REQUESTED",
  DATA_SYNC_COMPLETED = "DATA_SYNC_COMPLETED",
  DATA_SYNC_FAILED = "DATA_SYNC_FAILED",

  // State management
  STATE_CHANGED = "STATE_CHANGED",
  STATE_SYNC_REQUESTED = "STATE_SYNC_REQUESTED",
  STATE_SYNC_RESPONSE = "STATE_SYNC_RESPONSE",
}

/**
 * Event routing strategies
 */
export enum EventRoutingStrategy {
  DIRECT = "DIRECT",
  BROADCAST = "BROADCAST",
  FAN_OUT = "FAN_OUT",
  CHAIN = "CHAIN",
  PARALLEL = "PARALLEL",
}

/**
 * Module health status
 */
export enum ModuleHealthStatus {
  HEALTHY = "HEALTHY",
  UNHEALTHY = "UNHEALTHY",
  UNKNOWN = "UNKNOWN",
}

/**
 * Module interaction tracking
 */
export interface ModuleInteraction {
  id: string;
  sourceModule: string;
  targetModule: string;
  eventType: CrossModuleEvent;
  handler: (payload: CrossModulePayload) => Promise<void>;
  registeredAt: Date;
  callCount: number;
  lastCalled: Date | null;
  successCount: number;
  errorCount: number;
  routingPattern?: EventRoutingPattern;
  averageLatency: number;
  lastLatency: number;
}

/**
 * Module dependency tracking
 */
export interface ModuleDependency {
  targetModule: string;
  dependencyType: "required" | "optional" | "circular";
  version?: string;
  healthCheck?: boolean;
}

/**
 * Module state tracking
 */
export interface ModuleState {
  moduleName: string;
  status: "active" | "inactive" | "error";
  lastActivity: Date;
  dependencies: ModuleDependency[];
  interactions: ModuleInteraction[];
  healthStatus: ModuleHealthStatus;
  performanceMetrics: PerformanceMetrics;
}

/**
 * Performance metrics for modules
 */
export interface PerformanceMetrics {
  averageLatency: number;
  totalEvents: number;
  errorRate: number;
  lastUpdated: Date;
}

/**
 * Event routing pattern
 */
export interface EventRoutingPattern {
  pattern: string;
  strategy: EventRoutingStrategy;
  priority: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conditions?: Record<string, any>;
  fallback?: string;
}

/**
 * Cross-module payload structure
 */
export interface CrossModulePayload {
  eventId?: string;
  sourceModule?: string;
  targetModule?: string;
  timestamp?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Integration configuration
 */
export interface IntegrationConfig {
  enableCrossModuleEvents: boolean;
  enableDependencyTracking: boolean;
  enableMetrics: boolean;
  enableStateSynchronization: boolean;
  enableAdvancedRouting: boolean;
  enablePerformanceMonitoring: boolean;
  enableHealthChecks: boolean;
  enableEventRetry: boolean;
  maxEventRetries: number;
  eventTimeout: number;
  maxQueueSize: number;
  batchProcessingSize: number;
  healthCheckInterval: number;
  performanceThreshold: number;
}

/**
 * Integration metrics
 */
export interface IntegrationMetrics {
  totalCrossModuleEvents: number;
  successfulEvents: number;
  failedEvents: number;
  moduleInteractions: number;
  dependencyResolutions: number;
  averageEventLatency: number;
  eventQueueSize: number;
  stateSyncRequests: number;
  stateSyncResponses: number;
  routingPatternMatches: number;
  retryAttempts: number;
  healthCheckFailures: number;
  performanceViolations: number;
}

/**
 * State synchronization request
 */
export interface StateSyncRequest {
  requestId: string;
  timestamp: string;
  requestingModule: string;
  targetModules: string[];
}

/**
 * State synchronization response
 */
export interface StateSyncResponse {
  requestId: string;
  moduleName: string;
  state: ModuleState;
  timestamp: string;
}

/**
 * Module event map for type safety
 */
export interface ModuleEventMap {
  [CrossModuleEvent.USER_ORGANIZATION_JOINED]: {
    userId: string;
    organizationId: string;
    role: string;
  };
  [CrossModuleEvent.USER_ORGANIZATION_LEFT]: {
    userId: string;
    organizationId: string;
  };
  [CrossModuleEvent.USER_ORGANIZATION_ROLE_CHANGED]: {
    userId: string;
    organizationId: string;
    oldRole: string;
    newRole: string;
  };
  [CrossModuleEvent.USER_PROJECT_ACCESS_GRANTED]: {
    userId: string;
    projectId: string;
    permissions: string[];
  };
  [CrossModuleEvent.USER_PROJECT_ACCESS_REVOKED]: {
    userId: string;
    projectId: string;
  };
  [CrossModuleEvent.USER_PROJECT_ROLE_CHANGED]: {
    userId: string;
    projectId: string;
    oldRole: string;
    newRole: string;
  };
  [CrossModuleEvent.USER_APPLICATION_ACCESS_GRANTED]: {
    userId: string;
    applicationId: string;
    permissions: string[];
  };
  [CrossModuleEvent.USER_APPLICATION_ACCESS_REVOKED]: {
    userId: string;
    applicationId: string;
  };
  [CrossModuleEvent.USER_APPLICATION_ROLE_CHANGED]: {
    userId: string;
    applicationId: string;
    oldRole: string;
    newRole: string;
  };
  [CrossModuleEvent.ORGANIZATION_PROJECT_CREATED]: {
    organizationId: string;
    projectId: string;
    projectName: string;
  };
  [CrossModuleEvent.ORGANIZATION_PROJECT_DELETED]: {
    organizationId: string;
    projectId: string;
  };
  [CrossModuleEvent.ORGANIZATION_PROJECT_UPDATED]: {
    organizationId: string;
    projectId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updates: Record<string, any>;
  };
  [CrossModuleEvent.ORGANIZATION_APPLICATION_CREATED]: {
    organizationId: string;
    applicationId: string;
    applicationName: string;
  };
  [CrossModuleEvent.ORGANIZATION_APPLICATION_DELETED]: {
    organizationId: string;
    applicationId: string;
  };
  [CrossModuleEvent.ORGANIZATION_APPLICATION_UPDATED]: {
    organizationId: string;
    applicationId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updates: Record<string, any>;
  };
  [CrossModuleEvent.PROJECT_APPLICATION_CREATED]: {
    projectId: string;
    applicationId: string;
    applicationName: string;
  };
  [CrossModuleEvent.PROJECT_APPLICATION_DELETED]: {
    projectId: string;
    applicationId: string;
  };
  [CrossModuleEvent.PROJECT_APPLICATION_UPDATED]: {
    projectId: string;
    applicationId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updates: Record<string, any>;
  };
  [CrossModuleEvent.AUTH_USER_LOGIN]: {
    userId: string;
    timestamp: string;
  };
  [CrossModuleEvent.AUTH_USER_LOGOUT]: {
    userId: string;
    timestamp: string;
  };
  [CrossModuleEvent.AUTH_USER_SESSION_EXPIRED]: {
    userId: string;
    timestamp: string;
  };
  [CrossModuleEvent.DATA_SYNC_REQUESTED]: {
    moduleName: string;
    syncType: string;
  };
  [CrossModuleEvent.DATA_SYNC_COMPLETED]: {
    moduleName: string;
    syncType: string;
    recordsCount: number;
  };
  [CrossModuleEvent.DATA_SYNC_FAILED]: {
    moduleName: string;
    syncType: string;
    error: string;
  };
  [CrossModuleEvent.STATE_CHANGED]: {
    moduleName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any;
  };
  [CrossModuleEvent.STATE_SYNC_REQUESTED]: StateSyncRequest;
  [CrossModuleEvent.STATE_SYNC_RESPONSE]: StateSyncResponse;
}

/**
 * Module integration information
 */
export interface ModuleIntegrationInfo {
  moduleName: string;
  status: ModuleIntegrationStatus;
  dependencies: ModuleDependency[];
  interactions: ModuleInteraction[];
  lastActivity: Date | null;
  errorCount: number;
  successCount: number;
}

/**
 * Module integration status
 */
export enum ModuleIntegrationStatus {
  NOT_INTEGRATED = "NOT_INTEGRATED",
  INTEGRATING = "INTEGRATING",
  INTEGRATED = "INTEGRATED",
  ERROR = "ERROR",
}

/**
 * Integration error types
 */
export enum IntegrationErrorType {
  EVENT_EMISSION_FAILED = "EVENT_EMISSION_FAILED",
  EVENT_HANDLING_FAILED = "EVENT_HANDLING_FAILED",
  DEPENDENCY_RESOLUTION_FAILED = "DEPENDENCY_RESOLUTION_FAILED",
  STATE_SYNC_FAILED = "STATE_SYNC_FAILED",
  ROUTING_FAILED = "ROUTING_FAILED",
  PERFORMANCE_VIOLATION = "PERFORMANCE_VIOLATION",
  HEALTH_CHECK_FAILED = "HEALTH_CHECK_FAILED",
}

/**
 * Integration error class
 */
export class IntegrationError extends Error {
  public readonly type: IntegrationErrorType;
  public readonly moduleName?: string;
  public readonly eventType?: CrossModuleEvent;

  constructor(
    type: IntegrationErrorType,
    message: string,
    moduleName?: string,
    eventType?: CrossModuleEvent,
  ) {
    super(message);
    this.name = "IntegrationError";
    this.type = type;
    this.moduleName = moduleName;
    this.eventType = eventType;
  }
}
